/**
 * The picker's own sort and filters, remembered separately from Explore.
 *
 * Zoom moved out to utils/zoomPreference.js, where there is one of it for the
 * whole app. Sort and filter are about the task and stay per place; zoom is
 * about your eyes and does not change between screens.
 *
 * Zoom, sort and filter are per-place in this app, and the picker is a place.
 * Somebody sorting by value while filling a chase binder is answering "which of
 * these is the expensive one", which is a different question from the order they
 * left Explore in, and reusing Explore's keys would mean picking cards quietly
 * re-ordered a screen they were not looking at.
 *
 * Its own localStorage key for the same reason, and one key rather than three so
 * a partial write cannot leave a zoom from today next to a sort from last month.
 */

import {
  DEFAULT_SEARCH_SORT,
  DEFAULT_CATALOG_SORT,
  EMPTY_CATALOG_FILTERS,
  normaliseFilters,
} from '../../services/cardData/catalogFilters';

const STORAGE_KEY = 'cardchase.wishPicker.prefs';

/**
 * Two sorts, not one.
 *
 * A search and one set's cards are different shapes and Explore already keeps
 * two orders for that reason. Best Match is only meaningful over a query, and
 * inside a single set the natural order is the printed card number - which is
 * what "Oldest Set" collapses to when every card shares one release date.
 */
export const DEFAULT_PICKER_PREFS = {
  searchSort: DEFAULT_SEARCH_SORT,
  setSort: 'set-oldest',
  filters: EMPTY_CATALOG_FILTERS,
};

/** Whatever is in storage, coerced into a shape the picker can render. */
export function readPickerPrefs() {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PICKER_PREFS };
    const saved = JSON.parse(raw) || {};
    return {
      searchSort: saved.searchSort || DEFAULT_SEARCH_SORT,
      setSort: saved.setSort || DEFAULT_CATALOG_SORT,
      filters: normaliseFilters(saved.filters),
    };
  } catch {
    // A corrupt preference must never be the reason somebody cannot pick cards.
    return { ...DEFAULT_PICKER_PREFS };
  }
}

export function writePickerPrefs(prefs) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      searchSort: prefs.searchSort || DEFAULT_SEARCH_SORT,
      setSort: prefs.setSort || DEFAULT_CATALOG_SORT,
      filters: normaliseFilters(prefs.filters),
    }));
  } catch {
    /* a full or blocked store is not worth an error on a picking screen */
  }
}


/**
 * The master-set browser's own zoom, sort and filters.
 *
 * A third place, and therefore a third key. Somebody shopping for the next set
 * to build is asking "what is even in this set", which is a different question
 * from the one they left Explore or the card picker on - and a sort chosen while
 * shopping must not quietly re-order either of them. Same shape as the picker's
 * so one reader and one writer serve both.
 */
const MASTER_BROWSE_KEY = 'cardchase.wishMasterBrowse.prefs';

export const DEFAULT_MASTER_BROWSE_PREFS = {
  searchSort: DEFAULT_SEARCH_SORT,
  // Inside one set the natural order is the printed card number, which is what
  // "Oldest Set" collapses to when every card shares one release date.
  setSort: 'set-oldest',
  filters: EMPTY_CATALOG_FILTERS,
};

export function readMasterBrowsePrefs() {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(MASTER_BROWSE_KEY);
    if (!raw) return { ...DEFAULT_MASTER_BROWSE_PREFS };
    const saved = JSON.parse(raw) || {};
    return {
      searchSort: saved.searchSort || DEFAULT_SEARCH_SORT,
      setSort: saved.setSort || DEFAULT_CATALOG_SORT,
      filters: normaliseFilters(saved.filters),
    };
  } catch {
    // A corrupt preference must never be the reason somebody cannot browse sets.
    return { ...DEFAULT_MASTER_BROWSE_PREFS };
  }
}

export function writeMasterBrowsePrefs(prefs) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(MASTER_BROWSE_KEY, JSON.stringify({
      searchSort: prefs.searchSort || DEFAULT_SEARCH_SORT,
      setSort: prefs.setSort || DEFAULT_CATALOG_SORT,
      filters: normaliseFilters(prefs.filters),
    }));
  } catch {
    /* a full or blocked store is not worth an error on a browsing screen */
  }
}
